import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { supabase } from "../../../supabase/supabase";
import { scheduledCallService } from "@/services/scheduledCallService";
import { useToast } from "@/components/ui/use-toast";
import { LoadingSpinner } from "@/components/ui/loading-spinner";

interface CancelLeadCallDialogProps {
  isOpen: boolean;
  onClose: () => void;
  callRequestId: string;
  leadName: string;
  onCancelled: () => void;
}

export function CancelLeadCallDialog({
  isOpen,
  onClose,
  callRequestId,
  leadName,
  onCancelled,
}: CancelLeadCallDialogProps) {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);

  const handleCancel = async () => {
    setIsLoading(true);

    try {
      await scheduledCallService.cancelScheduledCall(callRequestId);

      toast({
        title: "Success",
        description: "Call cancelled successfully",
      });

      onCancelled();
      onClose();
    } catch (error) {
      console.error("Error cancelling call:", error);
      toast({
        title: "Error",
        description: "Failed to cancel call. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AlertDialog open={isOpen} onOpenChange={onClose}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Cancel Scheduled Call</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to cancel the scheduled call with {leadName}?
            This action cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isLoading}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              e.preventDefault();
              handleCancel();
            }}
            disabled={isLoading}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {isLoading ? (
              <>
                <LoadingSpinner className="mr-2 h-4 w-4" />
                Cancelling...
              </>
            ) : (
              "Yes, cancel call"
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
